/**
 * @format
 */
import { PartnerSurveyService } from '@/scripts/services/partner-survey/partner-survey-service';
import { ExportPuppeterService } from '@/scripts/services/puppeteer/export-puppeter-service';
import { compact, cloneDeep, noop } from 'lodash';
import { CurrencyMixin } from '@/mixins/currency-mixin';

export default {
    mixins: [CurrencyMixin],

    data() {
        return {
            reportPreviewData: null,
            partnerAccountId: null,
        };
    },

    props: {
        instanceId: {
            type: Number,
            default: null,
        },
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },
    },

    methods: {
        getBrandingConfiguration() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Brandings?$filter=AgencyId eq ${this.agencyId}`)
                    .then(({ data: { value } }) => {
                        if (value && value.length > 0) {
                            const branding = value.find(
                                (branding) => branding.Type === 'ReportCover',
                            );

                            this.reportPreviewData = branding
                                ? JSON.parse(branding.Configuration)
                                : noop();
                        }
                        resolve();
                    })
                    .catch(reject);
            });
        },

        print(blob) {
            const fileURL = window.URL.createObjectURL(
                new Blob([blob], { type: 'application/pdf' }),
            );
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('target', '_blank');
            document.body.appendChild(fileLink);
            fileLink.click();
            fileLink.remove();
        },

        generateReportJSON(surveyResults) {
            const { surveyName, submissionDetails, submissionAnswers } = surveyResults;
            const pages = this.generatePages(submissionAnswers);
            const outputJson = {
                template: 'agency-partner-survey-individual-report',
                format: 'pdf',
                deliveryMethod: 'attachment',
                accountId: this.accountId,
                data: {
                    title: surveyName,
                    totalPages: pages.length ? pages.length + 1 : 2,
                    logoImageUrl: this.reportPreviewData ? this.reportPreviewData.logo.url : null,
                    reportPreviewData: this.reportPreviewData,
                    partnerInfo: this.generatePartnerInfo(submissionDetails),
                    firstPageData: pages.shift(),
                    pages: cloneDeep(pages),
                },
            };

            return outputJson;
        },

        generatePartnerInfo(submissionDetails) {
            const { assignedUsers, partnerLogo, partnerName, submittedDate } = submissionDetails;
            return {
                assignedUsers,
                partnerLogo,
                partnerName,
                submitted: {
                    date: submittedDate
                        ? submittedDate.local().format('MMM D, YYYY')
                        : this.$moment().format('MMM D, YYYY'),
                    time: submittedDate
                        ? submittedDate.local().format('h:mm A')
                        : this.$moment().format('h:mm A'),
                },
            };
        },

        generatePages(submissionAnswers) {
            let pages = [];
            const sectionsByPage = this.splitSectionsByContent(submissionAnswers);
            sectionsByPage.forEach((item) => {
                const currentPage = item.page;
                const pageIndex = pages.findIndex((page) => page.pageNumber === currentPage);

                item.section.formElements = item.section.formElements.map((element) => {
                    if (element.currency) {
                        const symbol = this.getFlag(element.currency).symbol;
                        element.answers = this.formatCurrency(Number(element.answers), symbol);
                    }

                    return element;
                });

                if (pageIndex === -1) {
                    const newPage = {
                        pageNumber: currentPage,
                        sections: [item.section],
                    };
                    pages.push(newPage);
                } else {
                    pages[pageIndex].sections.push(item.section);
                }
            });

            return pages;
        },

        splitSectionsByContent(submisisonAnswers) {
            const sections = cloneDeep(submisisonAnswers);
            const sectionsChunks = [];
            let currentPage = 2; // Start from page 2 because page 1 is reserved for the cover
            let pageAvailableHeight = currentPage === 2 ? 1120 : 1840;

            sections.forEach((section) => {
                let doneProcessing = false;
                let mutableSection = section;
                let removedElements = [];

                while (!doneProcessing) {
                    let height = this.getSectionHeight(mutableSection);
                    let fitsInPage = pageAvailableHeight - height > 0;
                    if (fitsInPage) {
                        sectionsChunks.push({
                            section: cloneDeep(mutableSection),
                            page: currentPage,
                        });
                        pageAvailableHeight -= height;

                        if (!removedElements.length) {
                            doneProcessing = true;
                        } else {
                            mutableSection = {
                                id: mutableSection.id,
                                formElements: removedElements,
                            };
                            removedElements = [];
                            currentPage++;
                            pageAvailableHeight = currentPage === 2 ? 1130 : 1800;
                        }
                    } else {
                        removedElements.push(mutableSection.formElements.pop());
                    }
                }
            });

            return sectionsChunks;
        },

        getSectionHeight(section) {
            let height = 130; // Initial card height based on paddings + margins
            section.sectionTitle && (height += 32);
            section.sectionSubtitle && (height += 32);
            section.formElements.forEach((element) => {
                height += this.getElementHeight(element);
            });
            return height;
        },

        getElementHeight(element) {
            let height = 60; // Initial row height based on paddings + margins
            let fileRows = 0;
            element.label && (height += 34);
            switch (element.type) {
                case 'one-line':
                    element.answers && (height += 24);
                    break;
                case 'multi-selection':
                    element.answers.forEach(() => {
                        height += 24;
                    });
                    height += (element.answers.length - 1) * 10;
                    break;
                case 'rate':
                    height += 50;
                    break;
                case 'file-upload':
                    fileRows = Math.ceil(element.fileUploads.length / 4);
                    height += fileRows * 70;
                    height += fileRows ? (fileRows - 1) * 10 : 0;
                    break;
            }
            return height;
        },

        handleExportPartner(exportData) {
            const { exportSource, itemId, surveyId, partnerAccountId, loader } = exportData;
            if (exportSource !== 'board') {
                this[loader] = true;
            } else {
                this.processingItems = [{ id: itemId }];
            }

            this.getBrandingConfiguration()
                .then(() => {
                    return PartnerSurveyService.getIndividualSurveyResults({
                        accountId: this.accountId,
                        id: surveyId,
                        options: {
                            custom: `partnerAccountId=${partnerAccountId}`,
                        },
                    });
                })
                .then(({ data }) => {
                    const surveyResults = this.mapIndividualResponse(data);
                    const reportData = this.generateReportJSON(surveyResults);
                    return ExportPuppeterService.generate(reportData);
                })
                .then(({ data }) => {
                    this.print(data);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to export report partner.', {
                        error,
                    });
                })
                .finally(() => {
                    if (exportSource !== 'board') {
                        this[loader] = false;
                    } else {
                        this.processingItems = [];
                    }
                });
        },

        mapIndividualResponse({ data, total_submissions }) {
            const [submission] = data.data;
            const surveyInfo = data.surveyInfo;
            const totalSubmissions = total_submissions;
            this.partnerAccountId = submission ? submission.PartnerAccountId : null;

            if (submission) {
                const {
                    AssignedUsers,
                    Answers,
                    Partner,
                    Status,
                    StartDate,
                    SubmittedDate,
                    UpdatedAt,
                    AVGTime,
                } = submission;
                const { SurveyTemplate } = data.surveyInfo;
                const { sections } = JSON.parse(SurveyTemplate);
                const submisisonAnswers = Answers.map((answer) =>
                    this.mapSubmissionAnswersByType(answer),
                );

                const mappedSections = sections.map((section) => {
                    const sectionElementIds = section.columns.flatMap((column) =>
                        column.elements.map((element) => element.id),
                    );

                    return {
                        id: section.id,
                        sectionTitle: section.hasTitle ? section.title : null,
                        sectionSubtitle: section.hasDescription ? section.description : null,
                        formElements: this.getFormElementsBySection(
                            sectionElementIds,
                            submisisonAnswers,
                        ),
                    };
                });

                const submissionDetails = {
                    partnerLogo: Partner.LogoImageUrl,
                    partnerName: Partner.Name,
                    assignedUsers: this.getAssignedUser(AssignedUsers),
                    status: Status,
                    startDate: StartDate ? this.$moment(StartDate) : null,
                    submittedDate: SubmittedDate ? this.$moment(SubmittedDate) : null,
                    lastModified: UpdatedAt ? this.$moment(UpdatedAt) : null,
                    timeSpent: this.$helpers.secondsToHMS(AVGTime),
                };

                return {
                    surveyName: surveyInfo.Name,
                    submissionDetails,
                    submissionAnswers: mappedSections,
                    totalSubmissions,
                };
            } else {
                return {
                    surveyName: surveyInfo.Name,
                    submissionDetails: {},
                    submissionAnswers: [],
                    totalSubmissions,
                };
            }
        },

        getAssignedUser(assignedUsers) {
            return assignedUsers
                ? assignedUsers.map((user) => ({
                      id: user.Id,
                      name: `${user.FirstName} ${user.LastName}`,
                      profilePicture: user.ProfileImageUrl || null,
                  }))
                : [];
        },

        mapSubmissionAnswersByType(answer) {
            let fieldType = answer.Field.Type.Name;
            switch (fieldType) {
                case 'Text':
                case 'Date':
                case 'Number':
                case 'Currency':
                    return this.mapSingleAnswerField(answer);
                case 'CheckBox':
                case 'Radio':
                case 'DropDown':
                    return this.mapMultiSelectionField(answer);
                case 'Stars':
                    return this.mapRateField(answer);
                case 'File':
                    return this.mapFileUploadField(answer);
                default:
                    return null;
            }
        },

        mapSingleAnswerField({ Field, Value, Currency }) {
            return {
                frontendId: Field.FrontEndId,
                type: 'one-line',
                label: Field.Label,
                required: Field.IsRequired,
                multiline: false,
                isPercentage: Field.IsPercentage,
                answers: Field.DateFormat
                    ? this.$moment(Value, Field.DateFormat).format('MMM D, YYYY').toUpperCase()
                    : Value,
                currency: Currency,
            };
        },

        mapMultiSelectionField({ Field, Details, Value, None }) {
            const answers = Details.map(({ Value }) => Value);
            const noneValue = None ? Field.NoneLabel : null;
            return {
                frontendId: Field.FrontEndId,
                type: 'multi-selection',
                label: Field.Label,
                required: Field.IsRequired,
                answers: compact([Value, noneValue, ...answers]),
            };
        },

        mapRateField({ Field, Value }) {
            return {
                frontendId: Field.FrontEndId,
                type: 'rate',
                label: Field.Label,
                required: Field.IsRequired,
                rate: parseInt(Value),
            };
        },

        mapFileUploadField({ Field, Details }) {
            return {
                frontendId: Field.FrontEndId,
                type: 'file-upload',
                label: Field.Label,
                required: Field.IsRequired,
                fileUploads: Details.map((file) => ({
                    name: file.FileName,
                    type: file.MIMEType,
                    documentType: this.$helpers.getDocumentTypeFromMimeType(file.MIMEType),
                    size: file.FileSize,
                })),
            };
        },

        getFormElementsBySection(sectionElementIds, submissionAnswers) {
            const sectionElements = sectionElementIds.map((id) =>
                submissionAnswers.find((answer) => answer.frontendId === id),
            );
            return compact(sectionElements);
        },
    },
};
